import { useTranslation } from "react-i18next";
import { Lock } from "lucide-react";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * "Someone is editing this right now" (ADR-026).
 *
 * Shown on a manual card/row so an author sees a manual is taken **before** opening it, rather than being refused on
 * entry with "this manual is being edited by {name}". A padlock is the legacy's own vocabulary for this state.
 *
 * **It deliberately does not name the holder.** The list DTO carries a boolean and nothing else, because that payload
 * goes to everyone who can *view* the manual, while the holder's identity is exactly what the gated `getManualLock`
 * withholds from a non-editor. The name appears where authorization is checked — on the editor-entry dialog — not on a
 * card any viewer can see.
 *
 * Follows {@link ManualStateBadge}: a glyph AND a tint, never colour alone (colour-blindness + the brand-red clash),
 * and the explanation is a `Tooltip`, never a native `title`.
 */
export function ManualLockBadge({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <TooltipTrigger delay={300} excludeFromTabOrder>
      <span
        // `text-attention` (amber) rather than destructive: a held lock is informational, not an error — the manual is
        // being worked on, which is normal and healthy.
        className={cn(
          "bg-attention/10 text-attention inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[0.7rem] font-medium",
          className,
        )}
        // The glyph alone conveys the state visually; screen readers get the same meaning as the tooltip text.
        aria-label={t("manualManagement.badge.currentlyEditing")}
      >
        <Lock aria-hidden="true" className="size-3" />
        {t("manualManagement.badge.editing")}
      </span>
      <Tooltip>{t("manualManagement.badge.someoneEditing")}</Tooltip>
    </TooltipTrigger>
  );
}
